<template>
  <v-card align="center" justify="center" light max-width="56vw" min-width="30vw" outlined>
    <v-card-title style="background-color: #d2d7e1">
      <v-list-item>
        <v-list-item-title><h2>{{ $t('title') }}</h2></v-list-item-title>
        <v-spacer/>
        <v-list-item-action>
          <v-icon class="icon" icon @click="close">fa-times</v-icon>
        </v-list-item-action>
      </v-list-item>
    </v-card-title>
    <v-card-text class="py-7">
      <v-overlay :absolute="true" :value="overlayProcessing"><v-progress-circular :size="70" width="5" indeterminate/></v-overlay>
      <v-stepper v-model="step" class="elevation-0">
        <v-stepper-header class="elevation-0">
          <v-stepper-step :complete="step > 1" step="1">{{ $t('steps.basic') }}</v-stepper-step>
          <v-stepper-step :complete="step > 2" step="2">{{ $t('steps.details') }}</v-stepper-step>
          <v-stepper-step :complete="step > 3" step="3">{{ $t('steps.rules') }}</v-stepper-step>
          <v-stepper-step :complete="step > 4" step="4">{{ $t('steps.awards') }}</v-stepper-step>
          <v-stepper-step :complete="step > 5" step="5">{{ $t('steps.confirmation') }}</v-stepper-step>
        </v-stepper-header>
        <v-stepper-items class="mt-n5">
          <v-stepper-content step="1">
            <v-form v-model="validOne">
              <p class="text-center mb-7">{{ $t('basic.message') }}</p>
              <v-text-field :label="$t('basic.labelName')" :placeholder="$t('basic.placeholderName', { name: this.$store.state.clubMember.club.name })"
                            v-model="tournamentName" :rules="tournamentNameRules" counter="45" outlined/>
              <div class="d-flex align-center">
                <v-menu :close-on-content-click="false" offset-y transition="scale-transition" v-model="menuDates">
                  <template v-slot:activator="{ on }">
                    <v-text-field :label="$t('basic.labelDates')" outlined persistent-hint readonly v-model="datesRangeText" :rules="dateRules" v-on="on"/>
                  </template>
                  <v-date-picker @input="inputDates" range no-title v-model="tournamentDates"/>
                </v-menu>
                <v-select :items="platforms" :label="$t('basic.labelPlatform')" class="ml-3" v-model="tournamentPlatform" :rules="tournamentNameRules" outlined/>
              </div>
              <v-text-field :label="$t('basic.labelFAQs')" :placeholder="$t('basic.placeholderFAQs', { name: this.$store.state.clubMember.club.name.toLowerCase() })"
                            :hint="$t('basic.hintFAQs')" persistent-hint v-model="tournamentFAQs" :rules="tournamentFAQsRules" counter="100" outlined/>
              <v-btn @click="goNext" class="mt-7 mb-1">{{ $t('btnNext') }} »</v-btn>
              <v-card v-if="isFeatureUnavailable" class="d-flex align-center justify-center mt-3 rounded-card" color="error">
                <p class="pt-4 white-text">{{ $t('basic.tournamentsLimitReached') }}</p>
                <v-chip class="ml-3" text-color="black" @click="missingFeature">{{ $t('basic.exclusiveFeature') }}</v-chip>
              </v-card>
            </v-form>
          </v-stepper-content>
          <v-stepper-content step="2">
            <div class="d-flex align-center my-2">
              <h4 class="mr-5">{{ $t('details.visibility') }}</h4><v-divider/>
            </div>
            <v-radio-group v-model="tournamentType">
              <v-radio value="PRIVATE">
                <template v-slot:label><div><p class="mb-0 black--text">{{ $t('details.private') }}</p>{{ $t('details.privateDescription') }}</div></template>
              </v-radio>
              <v-radio value="PUBLIC" disabled>
                <template v-slot:label><div><p class="mb-0 black--text">{{ $t('details.public') }}<strong class="success--text">({{ $t('details.soon') }})</strong></p>{{ $t('details.publicDescription') }}</div></template>
              </v-radio>
              <v-radio value="EXCLUSIVE" disabled>
                <template v-slot:label><div><p class="mb-0 black--text">{{ $t('details.exclusive') }}<strong class="success--text">({{ $t('details.soon') }})</strong></p>{{ $t('details.exclusiveDescription') }}</div></template>
              </v-radio>
            </v-radio-group>
            <div class="d-flex align-center my-2">
              <h4 class="mr-5">{{ $t('details.rankType') }}</h4><v-divider/>
            </div>
            <v-radio-group v-model="tournamentRanking">
              <v-radio value="SWISET_RATIO">
                <template v-slot:label><div><p class="mb-0 black--text">{{ $t('details.swisetRatio') }} <strong class="success--text">({{ $t('details.recommended') }})</strong></p>{{ $t('details.swisetRatioDescription') }}</div></template>
              </v-radio>
              <v-radio value="BALANCE">
                <template v-slot:label><div><p class="mb-0 black--text">{{ $t('details.balance') }}</p>{{ $t('details.balanceDescription') }}</div></template>
              </v-radio>
            </v-radio-group>
            <div class="d-flex align-center justify-center mt-7 mb-1">
              <v-btn @click="goBack">« {{ $t('btnBack') }}</v-btn>
              <v-btn class="ml-3" @click="goNext">{{ $t('btnNext') }} »</v-btn>
            </div>
          </v-stepper-content>
          <v-stepper-content step="3">
            <v-form v-model="validThree">
              <p class="text-center mb-7">{{ $t('rules.message') }}</p>
              <div class="d-flex align-center justify-start mt-2">
                <h4 class="text-start">{{ $t('rules.initialBalance') }}<br><span>{{ $t('rules.initialBalanceDescription') }}</span></h4>
                <v-switch v-model="initialBalance" class="ml-4" inset :label="`${this.$t((initialBalance)? 'rules.selected' : 'rules.notSelected')}`"/>
                <v-text-field v-if="initialBalance" class="ml-5 mt-0 mb-n7" :label="$t('rules.initialBalance')" placeholder="E.g. 1000" type="number" v-model="initialBalanceNumber" dense outlined/>
              </div>
              <div class="d-flex align-center justify-start mt-2">
                <h4 class="text-start">{{ $t('rules.minDays') }} <strong class="success--text">({{ $t('details.recommended') }})</strong><br><span>{{ $t('rules.minDaysDescription') }}</span></h4>
                <v-switch v-model="minDaysTrading" class="ml-4" inset :label="`${this.$t((minDaysTrading)? 'rules.selected' : 'rules.notSelected')}`"/>
                <v-text-field v-if="minDaysTrading" class="ml-5 mt-0 mb-n7" :label="$t('rules.minDays')" placeholder="E.g. 15" type="number" v-model="minDaysTradingNumber" dense outlined/>
              </div>
              <div class="d-flex align-center justify-start mt-2">
                <h4 class="text-start">{{ $t('rules.consistency') }} <strong class="success--text">({{ $t('details.recommended') }})</strong><br><span>{{ $t('rules.consistencyDescription') }}</span></h4>
                <v-switch v-model="consistency" class="ml-4" inset :label="`${this.$t((consistency)? 'rules.selected' : 'rules.notSelected')}`"/>
              </div>
              <div class="d-flex align-center justify-start mt-2">
                <h4 class="text-start">{{ $t('rules.maxTradeSize') }}<br><span>{{ $t('rules.maxTradeSizeDescription') }}</span></h4>
                <v-switch v-if="tournamentPlatform === 'NinjaTrader'" v-model="maxTradeSize" class="ml-4" inset :label="`${this.$t((maxTradeSize)? 'rules.selected' : 'rules.notSelected')}`"/>
                <v-chip v-else class="ml-4 my-5" small>{{ $t('rules.onlyFor', { platform: 'NinjaTrader' }) }}</v-chip>
                <v-text-field v-if="maxTradeSize" class="ml-5 mt-0 mb-n7" :label="$t('rules.maxTradeSize')" placeholder="E.g. 5 contracts" type="number" v-model="maxTradeSizeNumber" dense outlined/>
              </div>
              <div class="d-flex align-center justify-start mt-2">
                <h4 class="text-start">{{ $t('rules.maxLoss') }}<br><span>{{ $t('rules.maxLossDescription') }}</span></h4>
                <v-switch v-if="tournamentPlatform === 'NinjaTrader'" v-model="maxMAE" class="ml-4" inset :label="`${this.$t((maxMAE)? 'rules.selected' : 'rules.notSelected')}`"/>
                <v-chip v-else class="ml-4 my-5" small>{{ $t('rules.onlyFor', { platform: 'NinjaTrader' }) }}</v-chip>
                <v-text-field v-if="maxMAE" class="ml-5 mt-0 mb-n7" :label="$t('rules.maxLoss')" placeholder="E.g. 500" type="number" v-model="maxMAENumber" dense outlined/>
              </div>
              <div class="d-flex align-center justify-start mt-2">
                <h4 class="text-start">{{ $t('rules.overnight') }}<br><span>{{ $t('rules.overnightDescription') }}</span></h4>
                <v-switch v-if="tournamentPlatform === 'NinjaTrader'" v-model="overnight" class="ml-4" inset :label="`${this.$t((overnight)? 'rules.withoutOvernight' : 'rules.withOvernight')}`"/>
                <v-chip v-else class="ml-4 my-5" small>{{ $t('rules.onlyFor', { platform: 'NinjaTrader' }) }}</v-chip>
              </div>
              <div class="d-flex align-center justify-center mt-10 mb-1">
                <v-btn @click="goBack">« {{ $t('btnBack') }}</v-btn>
                <v-btn class="ml-3" @click="goNext">{{ $t('btnNext') }} »</v-btn>
              </div>
            </v-form>
          </v-stepper-content>
          <v-stepper-content step="4">
            <v-form v-model="validFour">
              <p class="text-center mb-7">{{ $t('awards.message') }}</p>
              <div class="d-flex align-center justify-start mt-0" v-bind:key="award.rankNumber" v-for="award in awards">
                <h3 class="mt-n7">{{ award.rankNumber }}</h3>
                <v-text-field class="ml-3" :label="$t('awards.labelTitle', { position: award.rankNumber })" :placeholder="$t('awards.placeholderTitle')"
                              v-model="award.title" :rules="titleRule" dense outlined/>
                <v-text-field class="ml-2" :label="$t('awards.labelDescription', { position: award.rankNumber })"
                              :placeholder="$t('awards.placeholderDescription')" v-model="award.description" :rules="descriptionRule" dense outlined/>
                <v-icon v-if="award.rankNumber === awards.length && award.rankNumber !== 1" class="mt-n7 icon" style="width: 10%;" @click="DeleteLastAwards" small>fa-trash</v-icon>
              </div>
            </v-form>
            <v-btn style="width: 100%;" @click="AddAward">{{ $t('awards.btnAddAward') }} <v-icon class="ml-2" small>fa-plus</v-icon></v-btn>
            <div class="d-flex align-center justify-center mt-12 mb-1">
              <v-btn @click="goBack">« {{ $t('btnBack') }}</v-btn>
              <v-btn class="ml-3" @click="goNext">{{ $t('btnNext') }} »</v-btn>
            </div>
          </v-stepper-content>
          <v-stepper-content step="5">
            <p class="text-center mb-7">{{ $t('confirmation.message') }}</p>
            <v-img class="mb-5" src="https://swiset.com/wp-content/uploads/2021/01/Trading-Tournament.png" max-width="91px" contain/>
            <p class="text-start"><b>{{ $t('confirmation.name') }}:</b> {{ tournamentName }}.</p>
            <p class="text-start" v-if="formalStartDate !== null && formalEndDate !== null"><b>{{ $t('confirmation.dates') }}:</b>
              {{ $t('confirmation.datesText', { from: formalStartDate.toISOString().substr(0, 10), to: formalEndDate.toISOString().substr(0, 10), days: getTournamentDays() }) }}</p>
            <p class="text-start"><b>{{ $t('confirmation.details') }}:</b><v-chip class="ml-2" color="#00F784" small>
              {{ tournamentRanking.replace('_', ' ') }}</v-chip>
              {{ $t('confirmation.detailsTextPrepend') }} <v-chip color="#FFF75F" small>{{ tournamentType }}</v-chip> {{ $t('confirmation.detailsTextAppend') }}</p>
            <p class="text-start"><b>{{ $t('confirmation.rules') }}:</b> {{ $tc('confirmation.rulesText', rules.length, { count: rules.length }) }}</p>
            <p class="text-start"><b>{{ $t('confirmation.awards') }}:</b> {{ awards.length }} {{ $t('confirmation.awardsText') }}</p>
            <div class="d-flex align-center justify-center mt-12 mb-1">
              <v-btn @click="goBack">« {{ $t('btnBack') }}</v-btn>
              <v-btn class="ml-3" @click="createTournament">{{ $t('btnCreate') }}</v-btn>
            </div>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </v-card-text>
  </v-card>
</template>

<script>
import Repository from '../../../repositories/tournament';
import { mapMutations } from 'vuex';
export default {
  name: "AddTournament",
  props: {
    featurePermissionInfo: Object
  },
  data() {
    return {
      overlayProcessing: false,
      step: 1,
      validOne: false,
      validThree: false,
      validFour: false,

      // Step 1
      platforms: [
        'NinjaTrader',
        'cTrader',
        'MetaTrader 4',
        'MetaTrader 5',
      ],
      tournamentName: '',
      tournamentDates: [new Date().toISOString().substr(0, 10), new Date().toISOString().substr(0, 10)],
      tournamentPlatform: 'NinjaTrader',
      tournamentFAQs: '',
      menuDates: false,
      formalStartDate: null,
      formalEndDate: null,
      isFeatureUnavailable: false,
      tournamentNameRules: [
          t => !!t || this.$t('basic.ruleName'),
          t => (t && t.length <= 45) || 'Max 45 characters',
      ],
      dateRules: [
          d => !!d || this.$t('basic.ruleDates'),
      ],
      tournamentFAQsRules: [
          f => ((f && f.length <= 100 ) || f.length === 0)  || 'Max 100 characters',
      ],

      // Step 2
      tournamentType: 'PRIVATE',
      tournamentRanking: 'SWISET_RATIO',

      // Step 3
      initialBalance: false,
      initialBalanceNumber: 0,
      consistency: false,
      maxTradeSize: false,
      maxTradeSizeNumber: 0,
      maxMAE: false,
      maxMAENumber: 0,
      minDaysTrading: false,
      minDaysTradingNumber: 0,
      overnight: false,
      rules: [],

      // Step 4
      awards: [ { rankNumber: 1, title: '', description: '' }, ],
      titleRule: [
          t => !!t || this.$t('awards.ruleTitle'),
      ],
      descriptionRule: [
          d => !!d || this.$t('awards.ruleDescription'),
      ],
    };
  },
  computed: {
    datesRangeText () {
      return this.tournamentDates.join(` ${this.$t('basic.to')} `);
    },
  },
  methods: {
    goBack() {
      this.step--;
    },
    goNext() {
      if(this.step === 1 && this.validateBasics()) {
        if (this.featurePermissionInfo.unlimited == true) {
          this.step++;
        } else if (this.validateLimitAmountReached()) {
          this.isFeatureUnavailable = true;
        } else {
          this.step++;
        }
      } else if (this.step === 2 || (this.step === 3 && this.validateRules()) || (this.step === 4 && this.validateAwards()))
        this.step++;
    },
    missingFeature() {
      let plan = this.$store.state.clubMember.club.clubPackage.name;
      if(plan == 'Swiset custom') {
        window.open("https://t.me/Swiset", '_blank');
      } else {
        this.$router.push('/subscription-plans-club')
      }
    },
    validateLimitAmountReached() {
      let tournaments = this.$store.state.clubTournaments;
      let amountSelectedMonth = 0;
      let tournamentsLimitReached = false;
      for(let i = 0; i < tournaments.length && !tournamentsLimitReached; i++) {
        if (this.tournamentDates[0].split('-')[0] == tournaments[i].startTime.split('-')[0]  && this.tournamentDates[0].split('-')[1] == tournaments[i].startTime.split('-')[1]) {
          amountSelectedMonth++;
          tournamentsLimitReached = amountSelectedMonth >= this.featurePermissionInfo.amount;
        }
      }
      return tournamentsLimitReached;
    },
    inputDates(){
      if(this.tournamentDates.length === 2)
        this.menuDates = false;
    },
    DeleteLastAwards() {
      this.awards.pop();
    },
    AddAward() {
      this.awards.push( { rankNumber: this.awards.length + 1 , title: '', description: '' } );
    },
    validateBasics() {
      if(this.validOne && this.tournamentDates.length === 2){
        let startDateArray = this.tournamentDates[0].split('-');
        let endDateArray = this.tournamentDates[1].split('-');
        this.formalStartDate = new Date(startDateArray[0], startDateArray[1] - 1, startDateArray[2]);
        this.formalEndDate = new Date(endDateArray[0], endDateArray[1] - 1, endDateArray[2]);

        if(this.formalStartDate >= this.formalEndDate) {
          alert(this.$t('alerts.dateBefore'));
          return false;
        }
        else if(new Date() > this.formalStartDate || new Date() > this.formalEndDate) {
          alert(this.$t('alerts.dateToday'));
          return false;
        }

        return true;
      }
      else
        alert(this.$t('alerts.notFilled'));

      return false;
    },
    validateRules() {
      this.rules = [];
      if(this.tournamentRanking === 'BALANCE' && !this.initialBalance) {
        alert(this.$t('validation.initialBalanceRequired'));
        return false;
      }

      if(this.initialBalance && this.initialBalanceNumber <= 0) {
        alert(this.$t('validation.initialBalanceNumber'));
        return false;
      }

      if(this.consistency)
        this.rules.push({ ruleType: 'CONSISTENCY', value: 0.3 });

      if(this.maxTradeSize && this.maxTradeSizeNumber > 0)
        this.rules.push({ ruleType: 'MAX_TRADE_SIZE', value: this.maxTradeSizeNumber });
      else if(this.maxTradeSize){
        alert(this.$t('validation.tradeSizeNumber'));
        return false;
      }

      if(this.maxMAE && this.maxMAENumber > 0)
        this.rules.push({ ruleType: 'MAX_MAE', value: this.maxMAENumber });
      else if(this.maxMAE){
        alert(this.$t('validation.maxLossNumber'));
        return false;
      }

      if(this.minDaysTrading && this.minDaysTradingNumber > 0)
        this.rules.push({ ruleType: 'MIN_TRADABLE_DAYS', value: this.minDaysTradingNumber });
      else if(this.minDaysTrading){
        alert(this.$t('validation.minDaysNumber'));
        return false;
      }

      if(this.overnight)
        this.rules.push({ ruleType: 'OVERNIGHT', value: 1 });

      return true;
    },
    validateAwards() {
      if(!this.validFour)
        alert(this.$t('alerts.awards'));

      return this.validFour;
    },
    ...mapMutations(['setClubTournaments']),
    createTournament() {
      this.overlayProcessing = true;
      this.formalEndDate.setDate(this.formalEndDate.getDate() + 1);
      let tournament = {
        creator: { id: this.$store.state.clubMember.club.id },
        name: this.tournamentName,
        startTime: this.formalStartDate,
        endTime: this.formalEndDate,
        createdAt: new Date(),
        tournamentRankingType: this.tournamentRanking,
        tournamentVisibility: this.tournamentType,
      }
      if(this.initialBalance)
        tournament.initialBalance = this.initialBalanceNumber;
      if(this.tournamentFAQs !== null && this.tournamentFAQs !== '')
        tournament.faqsUrl = this.tournamentFAQs;

      let fullTournament = {
        tournament: tournament,
        rules: this.rules,
        awards: this.awards,
      };

      Repository.createFullTournament(fullTournament).then((response) => {
        if(response.status < 400) {
          alert(this.$t('alerts.created'));
          Repository.getTournamentsByClubId(this.$store.state.clubMember.club.id).then((secondResponse) => {
            if(secondResponse.status < 400)
              this.setClubTournaments(secondResponse.data);
            this.close();
          });
        }
        else {
          alert('An error occurred :(');
          this.overlayProcessing = false;
        }
      })
    },
    getTournamentDays() {
      if(this.formalStartDate !== null && this.formalEndDate !== null) {
        let Difference_In_Time = this.formalEndDate.getTime() - this.formalStartDate.getTime();
        return Difference_In_Time / (1000 * 3600 * 24);
      }
      return 0;
    },
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split('-');
      return `${month}/${day}/${year}`
    },
    parseDate(date) {
      if (!date) return null;
      const [month, day, year] = date.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    },
    close() {
      this.$emit('closeWindow');
    },
  }
}
</script>

<i18n>
{
  "en": {
    "title": "Create Tournament",
    "steps": {
      "basic": "Basic",
      "details": "Details",
      "rules": "Rules",
      "awards": "Awards",
      "confirmation": "Confirmation"
    },
    "basic": {
      "message":"Add the basic data for the tournament",
      "labelName": "Tournament Name",
      "ruleName": "Name is required",
      "placeholderName": "E.g. January {name} Tournament",
      "labelDates": "Tournament Dates",
      "ruleDates": "Days are required",
      "to": "to",
      "labelPlatform": "Tournament Platform",
      "labelFAQs": "Tournament FAQs (Optional)",
      "placeholderFAQs": "E.g. https://{name}.com/tournament",
      "hintFAQs": "Webpage with the most important answered questions about the tournament.",
      "tournamentsLimitReached": "You have reached the limit of tournaments created for this date",
      "exclusiveFeature": "Exclusive feature"
    },
    "details": {
      "soon": "Soon",
      "recommended": "Recommended",
      "visibility": "Select your tournament visibility",
      "private":"Private Tournament",
      "privateDescription":"Only your community users will be able to access the tournament.",
      "public":"Public Tournament",
      "publicDescription":"All Swiset users will be able to access the tournament.",
      "exclusive":"Exclusive Tournament",
      "exclusiveDescription":"Only the members you select will be part of the tournament.",
      "rankType": "Add the ranking type for the tournament",
      "swisetRatio": "Swiset Ratio",
      "swisetRatioDescription": "Ranking sorted by the participant Swiset Ratio.",
      "balance": "Balance",
      "balanceDescription": "Ranking sorted by the participant balance."
    },
    "rules": {
      "message": "Add rules and conditions for the tournament",
      "selected": "Selected",
      "notSelected": "Not Selected",
      "withOvernight": "With Overnight",
      "withoutOvernight": "Without Overnight",
      "initialBalance": "Initial Balance",
      "initialBalanceDescription": "Every participant starts with a balance.",
      "minDays": "Minimum Days of Trading",
      "minDaysDescription": "The min number of trading days per participant.",
      "consistency": "Consistency Rule",
      "consistencyDescription": "If selected, the max contribution per trade would be 30% of the total profit.",
      "maxTradeSize": "Maximum Trade Size",
      "maxTradeSizeDescription": "The max trade size accepted (Contracts).",
      "maxLoss": "Maximum Loss per Trade",
      "maxLossDescription": "The max loss accepted for each trade.",
      "overnight": "Overnight Trades?",
      "overnightDescription": "Decide if you accept overnight trades.",
      "onlyFor": "Only for {platform}"
    },
    "awards": {
      "message": "Add awards for the tournament participants",
      "labelTitle": "Title of {position}° award",
      "placeholderTitle": "E.g. 50K USD",
      "ruleTitle": "Title is required",
      "labelDescription": "Description of {position}° award",
      "placeholderDescription": "E.g. 50K USD funded account",
      "ruleDescription": "Description is required",
      "btnAddAward": "Add Award"
    },
    "confirmation": {
      "message": "Please confirm the tournament data",
      "name": "Tournament Name",
      "dates": "Tournament Dates",
      "datesText": "From {from} to {to} ({days} days).",
      "details": "Details",
      "detailsTextPrepend": "as ranking type and a",
      "detailsTextAppend": "tournament.",
      "rules": "Rules Selected",
      "rulesText": "No rules. | 1 rule. | {count} rules.",
      "awards": "Awards",
      "awardsText": "award(s) to be awarded."
    },
    "alerts": {
      "dateToday": "Dates SHOULD be after today.",
      "dateBefore": "Start Date SHOULD be before the End Date.",
      "notFilled": "All fields are required",
      "awards": "All the award details are required.",
      "created": "'Tournament was created successfully!'"
    },
    "validation": {
      "initialBalanceRequired": "Initial Balance is required if you select Balance as a Ranking Type.",
      "initialBalanceNumber": "Initial Balance requires a number higher than 0.",
      "tradeSizeNumber": "Max Trade Size requires a positive number.",
      "maxLossNumber": "Max Loss per Trade requires a positive number.",
      "minDaysNumber": "Min Days of Trading requires a positive number."
    },
    "btnBack": "Back",
    "btnNext": "Next",
    "btnCreate": "Create Tournament"
  },
  "es": {
    "title": "Crear Torneo",
    "steps": {
      "basic": "Básico",
      "details": "Detalles",
      "rules": "Reglas",
      "awards": "Premios",
      "confirmation": "Confirmación"
    },
    "basic": {
      "message":"Agregar la información básica del torneo",
      "labelName": "Nombre del Torneo",
      "ruleName": "Nombre es obligatorio",
      "placeholderName": "Ejemplo: Torneo de {name} Enero",
      "labelDates": "Fechas del Torneo",
      "ruleDates": "Días son obligatorios",
      "to": "al",
      "labelPlatform": "Plataforma del Torneo",
      "labelFAQs": "FAQs del Torneo (Opcional)",
      "placeholderFAQs": "Ejemplo: https://{name}.com/torneo",
      "hintFAQs": "Página web con las preguntas respondidas más importantes acerca del torneo.",
      "tournamentsLimitReached": "Has llegado al límite de torneos creados para esta fecha",
      "exclusiveFeature": "Funcionalidad exclusiva"
    },
    "details": {
      "soon": "Pronto",
      "recommended": "Recomendado",
      "visibility": "Selecciona la visibilidad de tu torneo",
      "private":"Torneo Privado",
      "privateDescription":"Únicamente los miembros de la comunidad podrán acceder al torneo.",
      "public":"Torneo Público",
      "publicDescription":"Todos los usuario de Swiset podrán acceder al torneo.",
      "exclusive":"Torneo Exclusivo",
      "exclusiveDescription":"Únicamente los miembros que tu selecciones podrán acceder al torneo.",
      "rankType": "Selecciona el tipo de ranking de tu torneo",
      "swisetRatio": "Ratio Swiset",
      "swisetRatioDescription": "Ranking ordenado por el Ratio Swiset de participantes.",
      "balance": "Balance",
      "balanceDescription": "Ranking ordenado por el Balance de participantes."
    },
    "rules": {
      "message": "Agrega reglas y condiciones para el torneo",
      "selected": "Seleccionado",
      "notSelected": "No Seleccionado",
      "withOvernight": "Con Overnight",
      "withoutOvernight": "Sin Overnight",
      "initialBalance": "Balance Inicial",
      "initialBalanceDescription": "Cada participante comienza con un balance.",
      "minDays": "Mínimos Días Operativa",
      "minDaysDescription": "Número mínimo de días de operativa por participante.",
      "consistency": "Regla de Consistencia",
      "consistencyDescription": "Si seleccionado, la máxima contribución por trade será el 30% del beneficio total.",
      "maxTradeSize": "Máximo Tamaño por Trade",
      "maxTradeSizeDescription": "Máximo tamaño aceptado por trade (Contratos).",
      "maxLoss": "Máxima Pérdida por Trade",
      "maxLossDescription": "Máxima pérdida aceptada por trade.",
      "overnight": "¿Trades Overnight?",
      "overnightDescription": "Decide si quieres aceptar trades overnight.",
      "onlyFor": "Sólo para {platform}"
    },
    "awards": {
      "message": "Agrega premios para los participantes del torneo",
      "labelTitle": "Título del premio #{position}",
      "placeholderTitle": "Ejemplo: 50K USD",
      "ruleTitle": "Título es obligatorio",
      "labelDescription": "Descripción del premio #{position}",
      "placeholderDescription": "Ejemplo: 50K USD en efectivo",
      "ruleDescription": "Descripción es obligatoria",
      "btnAddAward": "Agregar Premio"
    },
    "confirmation": {
      "message": "Por favor confirma los datos del torneo",
      "name": "Nombre del Torneo",
      "dates": "Fechas del Torneo",
      "datesText": "Del {from} al {to} ({days} días).",
      "details": "Detalles",
      "detailsTextPrepend": "como tipo de ranking y torneo",
      "detailsTextAppend": ".",
      "rules": "Reglas Seleccionadas",
      "rulesText": "Sin reglas. | 1 regla. | {count} reglas.",
      "awards": "Premios",
      "awardsText": "premio(s) a dar."
    },
    "alerts": {
      "dateToday": "Fechas DEBEN ser después del día de hoy.",
      "dateBefore": "Fecha de inicio DEBE ser antes de la fecha de finalización.",
      "notFilled": "Todos los campos son requeridos",
      "awards": "Todos los detalles del premio son requeridos",
      "created": "¡El torneo fue creado satisfactoriamente!"
    },
    "validation": {
      "initialBalanceRequired": "Balance Inicial es requerido cuando selecciona Balance en tipo de ranking.",
      "initialBalanceNumber": "Balance Inicial debe ser un número mayor a 0.",
      "tradeSizeNumber": "Máximo Tamaño de Trade debe ser un número mayor a 0.",
      "maxLossNumber": "Máxima Pérdida por Trade debe ser un número mayor a 0.",
      "minDaysNumber": "Mínimos Días de Operativa debe ser un número mayor a 0."
    },
    "btnBack": "Atrás",
    "btnNext": "Siguiente",
    "btnCreate": "Crear Torneo"
  }
}
</i18n>

<style scoped>

.white-text {
  color: white
}

.rounded-card {
  border-radius:15px
}

</style>
